import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from 'react-flexbox-grid';
import { FormattedMessage } from 'react-intl';
import { defaultTo, path, compose, is, filter } from 'ramda';

import HistoryCollapseContainer from 'containers/HistoryCollapseContainer';
import HistoryEntry from 'components/HistoryEntry';
import HistoryEntryHeader from 'components/HistoryEntryHeader';
import HistoryEntryColumns from 'components/HistoryEntryColumns';
import HistoryEntryColumn from 'components/HistoryEntryColumn';
import CommonHistoryEntryControls from 'routes/Documents/components/CommonHistoryEntryControls';
import BottomFormControls from 'routes/Documents/components/BottomFormControls';
import calc15D from './CalculatorField/calculate15D';
import calcEQ5D, { calculateEQ5DVAS } from './CalculatorField/calculateEQ5D';

import Paper from 'components/Paper';
import CommonHeader from 'routes/Documents/components/CommonHeader';
import CommonViewButton from 'routes/Documents/components/CommonViewButton';
import EditableV2 from 'routes/Documents/containers/EditableV2';

import Form from './Form';
import EQ5DForm from './EQ5DForm';
import InfoDialog15D from './InfoDialog15D';

import { formatHeaderDate, editTimeLeft } from 'lib/dateDisplay';

import { sectionHeader } from './QualityOfLife.less';
import { getLatestUnfinishedCommit, getLatestFinishedCommitData, getFirstCommitDate } from 'lib/documentHandling';
import { dateFromPartial } from 'neuro-utils';

const sortDocs = documents => documents.sort((d1, d2) => d2.createDate - d1.createDate);

const dimensions15D = [
  'mobility',
  'vision',
  'hearing',
  'breathing',
  'sleeping',
  'eating',
  'speech',
  'excretion',
  'usualActivities',
  'mentalFunction',
  'discomfortAndSymptoms',
  'depression',
  'distress',
  'vitality',
  'sexualActivity',
];

const dimensionsEQ5D = [
  'mobility',
  'selfcare',
  'usualActivities',
  'painDiscomfort',
  'anxietyDepression',
  'yourHealthToday',
];

// Calculates 15D score and change to previous score
const calculate15D = docs => {
  let prev = -1;
  let computed = 0;
  let change = 0;
  for (let i = docs.length - 1; i > -1; i--) {
    computed = calc15D(getLatestFinishedCommitData(docs[i]).sections);
    if (prev !== -1) {
      change = computed - prev;
    }
    prev = computed;
    docs[i].computed = computed;
    docs[i].change = change;
  }
  return docs;
};

// Calculates EQ5D score and change to previous score
const calculateEQ5D = docs => {
  let prevValue = -1;
  let prevVAS = 999;
  for (let i = docs.length - 1; i > -1; i--) {
    docs[i].computedValue = calcEQ5D(getLatestFinishedCommitData(docs[i]).sections);
    docs[i].changeValue = docs[i].computedValue && prevValue !== -1 ? docs[i].computedValue - prevValue : null;
    docs[i].VASValue = calculateEQ5DVAS(getLatestFinishedCommitData(docs[i]).sections ?? {});
    docs[i].changeVAS = docs[i].VASValue !== 999 && prevVAS !== 999 ? docs[i].VASValue - prevVAS : null;
    prevValue = docs[i].computedValue ? docs[i].computedValue : prevValue;
    prevVAS = docs[i].VASValue;
  }
  return docs;
};

const calculateEntries = d => {
  const dimensions = d.documentType === 'eq5d' ? dimensionsEQ5D : dimensions15D;
  let counter = 0;
  for (let i = 0; i < dimensions.length; i++) {
    if (safelyHandleValue(d, dimensions[i])) {
      counter++;
    }
  }
  return counter;
};

const changeDescription = c => {
  if (c > 0.035) return 'qualityoflife.description.muchBetter';
  if (c <= 0.035 && c >= 0.015) return 'qualityoflife.description.slightlyBetter';
  if (c < 0.015 && c > -0.015) return 'qualityoflife.description.muchTheSame';
  if (c >= -0.035 && c <= -0.015) return 'qualityoflife.description.slightlyWorse';
  if (c < -0.035) return 'qualityoflife.description.muchWorse';
};

const safelyHandleValue = (obj, val) => compose(defaultTo(null), path(['content', 'sections', val]))(obj);

const renderHeader = (headerText, section) => (
  <p title={section && section === 'eq5d' ? 'EQ-5D' : '15D'}>{headerText}</p>
);

const historyMapper15D = (name, edit, view) => (d, intl) => {
  const dateStamp = d?.content?.date ? dateFromPartial(d.content.date).getTime() : d.createDate;
  return (
    <HistoryEntry key={d.documentId}>
      <HistoryEntryHeader title={formatHeaderDate(dateStamp)} active={editTimeLeft(getFirstCommitDate(d.commits))}>
        {window.__ROLE__ !== 'DOCTOR' && editTimeLeft(getFirstCommitDate(d.commits)) && (
          <CommonHistoryEntryControls
            time={getFirstCommitDate(d.commits)}
            action={() => edit(name, d.documentId, getLatestFinishedCommitData(d))}
          />
        )}
        {(window.__ROLE__ === 'DOCTOR' || !editTimeLeft(getFirstCommitDate(d.commits))) && (
          <CommonViewButton onView={e => view('qualityoflife', d.documentId)} />
        )}
      </HistoryEntryHeader>
      <HistoryEntryColumns>
        <HistoryEntryColumn
          header={renderHeader(intl.formatMessage({ id: 'qualityoflife.labels.filled' }), d.documentType)}
          smallScaling={4}
        >
          <p>{calculateEntries(d) + ' / 15'}</p>
        </HistoryEntryColumn>
        <HistoryEntryColumn
          header={renderHeader(intl.formatMessage({ id: 'qualityoflife.labels.15D' }), d.documentType)}
          smallScaling={3}
        >
          <p>{is(Number, path(['computed'], d)) ? d.computed.toFixed(3) : null}</p>
        </HistoryEntryColumn>
        <HistoryEntryColumn
          header={renderHeader(intl.formatMessage({ id: 'qualityoflife.labels.change' }))}
          smallScaling={5}
        >
          <p>{!isNaN(d.change) ? d.change.toFixed(3) : null}</p>
        </HistoryEntryColumn>
        {!isNaN(d.change) && (
          <HistoryEntryColumn
            header={renderHeader(intl.formatMessage({ id: 'qualityoflife.labels.description' }), d.documentType)}
            smallScaling={12}
          >
            <p>{intl.formatMessage({ id: changeDescription(d.change) })}</p>
          </HistoryEntryColumn>
        )}
        {isNaN(d.change) && (
          <HistoryEntryColumn header="">
            <p>{null}</p>
          </HistoryEntryColumn>
        )}
      </HistoryEntryColumns>
    </HistoryEntry>
  );
};

const historyMapperEQ5D = (name, edit, view) => (d, intl) => (
  <HistoryEntry key={d.documentId}>
    <HistoryEntryHeader title={formatHeaderDate(d.createDate)} active={editTimeLeft(getFirstCommitDate(d.commits))}>
      {window.__ROLE__ !== 'DOCTOR' && editTimeLeft(getFirstCommitDate(d.commits)) && (
        <CommonHistoryEntryControls
          time={getFirstCommitDate(d.commits)}
          action={() => edit(name, d.documentId, getLatestFinishedCommitData(d))}
        />
      )}
      {(window.__ROLE__ === 'DOCTOR' || !editTimeLeft(getFirstCommitDate(d.commits))) && (
        <CommonViewButton onView={e => view(name, d.documentId)} />
      )}
    </HistoryEntryHeader>
    <HistoryEntryColumns>
      <HistoryEntryColumn
        header={renderHeader(intl.formatMessage({ id: 'qualityoflife.labels.filled' }), d.documentType)}
        smallScaling={4}
      >
        <p>{calculateEntries(d) + ' / 6'}</p>
      </HistoryEntryColumn>
      <HistoryEntryColumn
        header={renderHeader(intl.formatMessage({ id: 'qualityoflife.labels.EQ5D' }), d.documentType)}
        smallScaling={3}
      >
        <p>{is(Number, path(['computedValue'], d)) ? d.computedValue.toFixed(3) : null}</p>
      </HistoryEntryColumn>
      <HistoryEntryColumn
        header={renderHeader(intl.formatMessage({ id: 'qualityoflife.labels.EQ5DVAS' }), d.documentType)}
        smallScaling={5}
      >
        <p>{d.VASValue}</p>
      </HistoryEntryColumn>
      <HistoryEntryColumn
        header={renderHeader(intl.formatMessage({ id: 'qualityoflife.labels.change' }), d.documentType)}
        smallScaling={5}
      >
        <p>
          {(d.changeValue !== null ? d.changeValue.toFixed(3) : 'NA') +
            ' / ' +
            (d.changeVAS !== null ? d.changeVAS : 'NA')}
        </p>
      </HistoryEntryColumn>
    </HistoryEntryColumns>
  </HistoryEntry>
);

const QualityOfLife = ({ names, documents, editingDocument, viewId, onView, onStartEdit, onCreate }) => (
  <div>
    <Paper topMargin={false}>
      <Grid fluid>
        <CommonHeader
          names={names}
          localeId="qualityoflife.generic.title"
          infoText={
            (editingDocument && editingDocument.documentType === 'qualityoflife') ||
            (viewId && viewId.name === 'qualityoflife')
              ? 'qualityoflife.infoText'
              : 'qualityoflife.generic.infoText'
          }
          mode="create"
          editing={editingDocument}
          startEdit={onCreate}
        />
      </Grid>
      {editingDocument && editingDocument.documentType === 'qualityoflife' && (
        <EditableV2
          documentId={editingDocument.documentId}
          moduleName={'qualityoflife'}
          editedCommit={getLatestUnfinishedCommit(editingDocument)}
        >
          <Form />
        </EditableV2>
      )}
      {editingDocument && editingDocument.documentType === 'eq5d' && (
        <EditableV2
          documentId={editingDocument.documentId}
          moduleName={'eq5d'}
          editedCommit={getLatestUnfinishedCommit(editingDocument)}
        >
          <EQ5DForm />
        </EditableV2>
      )}

      {!editingDocument && viewId && viewId.name === 'qualityoflife' && (
        <Form
          editing={false}
          formValues={getLatestFinishedCommitData(documents.find(d => d.documentId === viewId.id))}
        />
      )}
      {!editingDocument && viewId && viewId.name === 'eq5d' && (
        <EQ5DForm
          editing={false}
          formValues={getLatestFinishedCommitData(documents.find(d => d.documentId === viewId.id))}
        />
      )}

      {!editingDocument && !viewId && (
        <div>
          <h4 className={sectionHeader}>
            <FormattedMessage id="qualityoflife.title" /> <InfoDialog15D buttonStyle={{ paddingLeft: '2rem' }} />
          </h4>
          <HistoryCollapseContainer
            documents={calculate15D(sortDocs(filter(d => d.documentType === 'qualityoflife', documents)))}
            emptyText="qualityoflife.emptyText"
            sorter={sortDocs}
            mapper={historyMapper15D(names[0], onStartEdit, onView)}
            limit={3}
          />
          <h4 className={sectionHeader}>
            <FormattedMessage id="eq5d.title" />
          </h4>
          <HistoryCollapseContainer
            documents={calculateEQ5D(sortDocs(filter(d => d.documentType === 'eq5d', documents)))}
            emptyText="qualityoflife.emptyText"
            sorter={sortDocs}
            mapper={historyMapperEQ5D(names[1], onStartEdit, onView)}
            limit={3}
          />
        </div>
      )}
    </Paper>
    {editingDocument && (
      <BottomFormControls
        name={editingDocument.documentType}
        editId={editingDocument.documentId}
        commitId={getLatestUnfinishedCommit(editingDocument).commitId}
      />
    )}
  </div>
);

QualityOfLife.propTypes = {
  names: PropTypes.array,
  documents: PropTypes.array,
  onStartEdit: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  viewId: PropTypes.object,
  onView: PropTypes.func,
  editingDocument: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
};

export default QualityOfLife;
